<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span class="title">{{ $t('title.software') }}</span>
        <div class="d-flex">
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                icon="mdi-eye" @click="printSoftware">
              </v-btn>
            </template>
            {{ $t('general.viewprint') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn" icon="mdi-plus"
                type="button" @click="addSoftware" />
            </template>
            {{ $t('general.addSoftware') }}
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" autocomplete="off"
          density="compact" />

        <v-data-table :headers="headers" :items="filteredSoftwares" :loading="loading"
          :loading-text="$t('general.loading')" class="min-width-table custom-header" :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-for="column in columns" :key="column.value" @click="() => column.sortable && toggleSort(column)"
                :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign:
                    column.value === 'actions'
                      ? 'center'
                      : column.value === 'statusName'
                        ? 'center'
                        : 'left'
                }" class="sortable-header">
                <div class="d-flex align-center w-100" :class="column.value === 'actions'
                  ? 'justify-center'
                  : column.value === 'statusName'
                    ? 'justify-center'
                    : 'justify-space-between'
                  ">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>
              </th>
            </tr>
          </template>

          <!-- Akcije -->
          <template v-slot:item="{ item }">
            <tr :class="rowClass(item)">
              <td>{{ item.Name }}</td>
              <td>{{ item.Verzija }}</td>
              <td>{{ item.Proizvodjac }}</td>
              <td>{{ item.TipLicence }}</td>
              <!-- STATUS (INLINE EDIT) -->
              <td class="text-center">
                <v-menu location="bottom" offset="4">
                  <template #activator="{ props }">
                    <v-btn v-bind="props" variant="text" class="status-btn">
                      <img v-if="parseInt(item.StatusId) === 1" :src="statusImages.aktivan" height="22" />
                      <img v-if="parseInt(item.StatusId) === 2" :src="statusImages.neaktivan" height="22" />
                      <img v-if="parseInt(item.StatusId) === 3" :src="statusImages.rashodovan" height="22" />
                    </v-btn>
                  </template>

                  <v-list density="compact">
                    <v-list-item v-for="s in statusOptions" :key="s.id" :disabled="s.id === item.StatusId"
                      @click="s.id !== item.StatusId && askConfirmStatusChange(item, s.id)">
                      <v-list-item-title class="d-flex align-center justify-space-between">
                        {{ s.name }}
                        <v-icon v-if="s.id === item.StatusId" size="14">
                          mdi-check
                        </v-icon>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>

              <!-- IKONICE -->
              <td>
                <div class="d-flex justify-center action-buttons">
                  <!-- EDIT -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn mr-2"
                        :disabled="(item.raw?.StatusId ?? item.StatusId) === 3" @click="editSoftware(item.raw ?? item)">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('general.uredi') }}
                  </v-tooltip>

                  <!-- POBRISI-->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="red" class="action-btn"
                        @click="openDeleteDialog(item)">
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    {{ $t('general.delete') }}
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </template>


        </v-data-table>

      </v-card-text>
    </v-card>

    <!-- Dijalog za dodavanje/uređivanje sredstva -->
    <BaseModal v-model="dialog" size="md"
      :title="editMode ? this.$t('modalni.software.uredi') : this.$t('modalni.software.dodaj')">
      <v-form ref="softwareForm" v-model="isFormValid">
        <!-- Naziv -->
        <v-text-field v-model="form.name" :label="$t('table.software.naziv')" :rules="[rules.required]" required
          class="my-text" />

        <!-- Verzija -->
        <v-text-field v-model="form.version" :label="$t('table.software.verzija')" class="my-text" />

        <!-- Proizvođač -->
        <v-text-field v-model="form.vendor" :label="$t('table.software.proizvodjac')" :rules="[rules.required]" required
          class="my-text" />

        <!-- Tip licence -->
        <v-text-field v-model="form.licence_type" :label="$t('table.software.tiplicence')" class="my-text" />

        <v-row dense>
          <!-- Datum nabavke licence -->
          <v-col cols="12" md="6">
            <v-text-field v-model="form.purchaseDate" :label="$t('table.software.datumNabavke')" type="date"
              :disabled="editMode" clearable clear-icon="mdi-close" class="my-text" />
          </v-col>

          <!-- Datum isticanja licence -->
          <v-col cols="12" md="6">
            <v-text-field v-model="form.expiryDate" :label="$t('table.software.datumIsticanja')" type="date" clearable
              clear-icon="mdi-close" class="my-text" />
          </v-col>
        </v-row>

        <!-- Status -->
        <v-select v-if="editMode" v-model="form.StatusId" :items="statusOptions" item-title="name" item-value="id"
          :label="$t('table.software.status')" density="comfortable" :rules="[rules.required]" required
          class="my-selectModal" />


        <!-- Napomena -->
        <v-textarea v-model="form.notes" :label="$t('table.software.napomena')" rows="3" auto-grow class="my-text" />
      </v-form>
      <!-- FOOTER -->
      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="outlined" @click="dialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <v-btn color="primary" variant="flat" @click="saveSoftware">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!-- Dijalog za potvrdu brisanja softvera -->
    <BaseModal v-model="deleteDialog" :title="$t('modalni.software.brisi')">
      <p>{{ $t('modalni.software.brisanjePoruka') }} <strong>{{ deleteSoftwareName }}</strong>?</p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDeleteSoftware">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ this.$t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!-- PROMJENA STATUSA -->
    <BaseModal v-model="confirmStatusDialog" :title="this.$t('modalni.smallAsset.promjenaStatusaModalni')">
      <p class="confirm-text" style="font-size: 14px;">
        {{ this.$t('modalni.software.promjenaStatusaPoruka1') }}<strong>{{ pendingStatusChange?.softwareName
          }}</strong>{{
            this.$t('modalni.software.promjenaStatusaPoruka2') }}
        <strong>
          {{statusOptions.find(s => s.id === pendingStatusChange?.newStatusId)?.name}}
        </strong>?
      </p>

      <template #footer>
        <div class="d-flex justify-end" style="gap: 12px;">
          <v-btn variant="outlined" @click="cancelStatusChange">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <v-btn color="primary" @click="confirmStatusChange">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.potvrdi') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!--Save/Print dialog-->
    <BaseModal v-model="reportPreview" size="xl" :title="$t('print.listaSoftvera')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadPDF">
              <v-icon size="18"
                style="padding-top: 2px !important; padding-left: 10px !important;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printReport">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

      </template>

      <iframe ref="reportFrame" :style="{
        width: '100%',
        height: '100%',
        border: 'none',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }" />

    </BaseModal>

  </v-container>
</template>

<script>
import BaseModal from '@/components/dialogs/BaseModal.vue'
import { printTable, fetchHeaderImage } from "@/services/printService";
import { format } from "date-fns";
import html2pdf from "html2pdf.js"

export default {
  name: "SoftwarePage",
  components: {
    BaseModal
  },
  data() {
    return {
      html: '',
      zoom: 1,
      reportPreview: false,
      confirmStatusDialog: false,
      pendingStatusChange: {
        asset: null,
        newStatusId: null,
        oldStatusId: null,
      },
      deleteDialog: false,
      deleteSoftwareId: null,
      deleteSoftwareName: "",
      isFormValid: false,
      search: "",
      loading: false,
      dialog: false,
      editMode: false,
      selectedId: null,
      softwares: [],
      statusOptions: [
        { id: 1, name: this.$t('general.aktivan') },
        { id: 2, name: this.$t('general.neaktivan') },
        { id: 3, name: this.$t('general.rashodovan') }
      ],
      // VALIDACIJA
      rules: {
        required: v => (v !== null && v !== undefined && v !== '') || this.$t('systemMessage.m1'),
      },
      form: {
        name: "",
        version: "",
        vendor: "",
        licence_type: "",
        notes: "",
        StatusId: null,
        purchaseDate: null,
        expiryDate: null,
      },
      headers: [
        { text: this.$t('table.software.naziv'), value: "Name", sortable: true, width: 'calc(50% - 100px)' },
        { text: this.$t('table.software.verzija'), value: "Verzija", sortable: true, width: "8%" },
        { text: this.$t('table.software.proizvodjac'), value: "Proizvodjac", sortable: true, width: "16.5%" },
        { text: this.$t('table.software.tiplicence'), value: "TipLicence", sortable: true, width: "15%" },
        { text: this.$t('table.software.status'), align: 'center', value: "StatusId", sortable: false, width: "130px" },
        { text: this.$t('general.optionsUPPER'), value: "actions", sortable: false, width: "100px" },
      ],
    };
  },
  computed: {
    currentLang() {
      const lang = localStorage.getItem('lang')
      if (lang === 'sr' || lang === 'cir-ekavica') return 'Cir'
      if (lang === 'en') return 'Eng'
      return 'Lat'
    },
    statusImages() {
      const lang = this.currentLang
      return {
        aktivan: `/img/aktivan${lang}.png`,
        neaktivan: `/img/neaktivan${lang}.png`,
        rashodovan: `/img/rashodovan${lang}.png`,
      }
    },

    filteredSoftwares() {
      const term = (this.search || "").toLowerCase().trim();
      return this.softwares.filter(sw =>
        String(sw.Name ?? "").toLowerCase().includes(term) ||
        String(sw.Verzija ?? "").toLowerCase().includes(term) ||
        String(sw.Proizvodjac ?? "").toLowerCase().includes(term) ||
        String(sw.TipLicence ?? "").toLowerCase().includes(term) ||
        String(sw.StatusName ?? "").toLowerCase().includes(term)
      )
    },
  },
  mounted() {
    this.fetchSoftwares();
  },
  methods: {
    generateReportFileName(title) {

      const date = new Date()

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      const compactDate = `${day}${month}${year}`

      const cleanTitle = title
        .replace(/[čć]/g, "c")
        .replace("ž", "z")
        .replace("š", "s")
        .replace("đ", "d")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

      return `${cleanTitle}_${compactDate}.pdf`
    },

    openReportPreview(html) {

      this.reportPreview = true

      this.$nextTick(() => {
        this.$nextTick(() => {

          const iframe = this.$refs.reportFrame

          if (!iframe) return

          const doc = iframe.contentWindow.document

          doc.open()
          doc.write(html)
          doc.close()

        })
      })
    },

    printReport() {
      const iframe = this.$refs.reportFrame
      iframe.contentWindow.print()
    },

    downloadPDF() {

      const container = document.createElement("div")
      container.innerHTML = this.html

      const title = container.querySelector("h2")?.innerText

      const fileName = this.generateReportFileName(title)

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape"
          }
        })
        .from(container)
        .save()
    },

    getStatusName(StatusId) {
      return this.statusOptions.find(s => s.id === StatusId)?.name || ''
    },

    async printSoftware() {
      const columns = [
        { key: "name", label: this.$t("table.software.naziv"), width: "33%" },
        { key: "version", label: this.$t("table.software.verzija"), width: "10%" },
        { key: "vendor", label: this.$t("table.software.proizvodjac"), width: "14%" },
        { key: "licenceType", label: this.$t("table.software.tiplicence"), width: "10%" },
        { key: "purchaseDate", label: this.$t("table.software.datumNabavke"), width: "12%" },
        { key: "expiryDate", label: this.$t("table.software.datumIsticanja"), width: "12%" },
        { key: "status", label: this.$t("table.software.status"), width: "9%", align: "center" },
      ];

      const data = this.filteredSoftwares.map(sw => ({
        name: sw.Name ?? "",
        version: sw.Verzija ?? "",
        vendor: sw.Proizvodjac ?? "",
        licenceType: sw.TipLicence ?? "",
        purchaseDate: this.formatDate(sw.Date),
        expiryDate: this.formatDate(sw.Expired),
        status: sw.StatusName ?? ""
      }));

      const headerImg = await fetchHeaderImage(this.$axios)
      const footer = `
    ${this.$t("exportToExcel.pokrenuo")} ${localStorage.getItem("Name")} <br />
    ${this.$t("print.m1")} ${format(new Date(), "dd.MM.yyyy.")}
  `;

      this.html = printTable({
        title: this.$t("print.listaSoftvera"),
        columns,
        data,
        headerImg,
        footer,
      });
      this.openReportPreview(this.html)
    },
    askConfirmStatusChange(item, newStatusId) {
      if (item.StatusId === newStatusId) return

      this.pendingStatusChange = {
        asset: item,
        softwareName: item.Name,
        oldStatusId: item.StatusId,
        newStatusId
      }

      this.confirmStatusDialog = true
    },
    async confirmStatusChange() {
      const { asset, newStatusId, oldStatusId } = this.pendingStatusChange
      if (!asset) return

      asset.StatusId = newStatusId

      try {
        await this.$axios.put('Software/EditStatus', {
          AssetId: asset.Id,
          StatusId: Number(newStatusId),
          CompanyId: Number(localStorage.getItem('CompanyId'))
        })

        asset.statusName =
          this.statusOptions.find(s => s.id === newStatusId)?.name || ''

        this.$store.dispatch("NOTIF_SUCCESS", {
          poruka: this.$t('User_message.m53'),
          position: "top-end",
          timer: 2000,
          action: "noreload"
        })

        await this.fetchSoftwares()

      } catch (err) {
        asset.StatusId = oldStatusId

        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m54'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      } finally {
        this.cancelStatusChange()
      }
    },
    cancelStatusChange() {
      this.confirmStatusDialog = false
      this.pendingStatusChange = {
        asset: null,
        softwareName: "",
        newStatusId: null,
        oldStatusId: null
      }
    },

    rowClass(item) {
      if (item.StatusId === 3) return 'row-rashodovano'
      if (item.StatusId === 2) return 'row-neaktivno'
      if (item.StatusId === 1) return 'row-aktivno'
      return ''
    },
    async changeSoftwareStatus(item, newStatusId) {
      if (item.StatusId === newStatusId) return

      const oldStatusId = item.StatusId
      item.StatusId = newStatusId

      try {
        await this.$axios.put('Software/EditStatus', {
          AssetId: item.id,
          StatusId: Number(newStatusId),
          CompanyId: Number(localStorage.getItem('CompanyId'))
        })

        item.statusName =
          this.statusOptions.find(s => s.id === newStatusId)?.name || ''
        await this.fetchSoftwares()

        this.$store.dispatch("NOTIF_SUCCESS", {
          poruka: this.$t('User_message.m53'),
          position: "top-end",
          timer: 2000,
          action: "noreload"
        })

      } catch (err) {
        item.StatusId = oldStatusId

        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m54'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      }
    },
    openDeleteDialog(item) {
      this.deleteSoftwareId = item.Id
      this.deleteSoftwareName = item.Name
      this.deleteDialog = true
    },

    async fetchSoftwares() {
      this.loading = true
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('Software/', data)

        this.softwares = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.softwares = []
      } finally {
        this.loading = false
      }
    },
    addSoftware() {
      this.editMode = false;
      this.selectedId = null;
      this.resetForm();
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.softwareForm?.resetValidation()
      })
    },
    editSoftware(item) {

      this.editMode = true;
      this.selectedId = item.Id;

      this.form = {
        name: item.Name,
        version: item.Verzija,
        vendor: item.Proizvodjac,
        licence_type: item.TipLicence,
        notes: item.Napomena,
        StatusId: item.StatusId,
        purchaseDate: item.Date || "",
        expiryDate: item.Expired || ""
      }

      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.softwareForm?.resetValidation()
      })
    },
    async confirmDeleteSoftware() {
      if (!this.deleteSoftwareId) return

      try {
        await this.$axios.put(
          'Software/Delete/' + this.deleteSoftwareId
        )

        this.$store.dispatch("NOTIF_SUCCESS", {
          poruka: this.$t('User_message.m49'),
          position: "top-end",
          timer: 2500,
          action: "noreload"
        })

        await this.fetchSoftwares()

      } catch (err) {
        console.error(err)

        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m50'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      } finally {
        this.deleteDialog = false
        this.deleteSoftwareId = null
        this.deleteSoftwareName = ""
      }
    },

    async saveSoftware() {
      const { valid } = await this.$refs.softwareForm.validate() // valiadcija forme
      if (!valid) return

      const now = new Date()
      const timePart = now.toTimeString().split(' ')[0]
      // BASE payload (bez Id)
      const payload = {
        Name: this.form.name,
        Verzija: this.form.version,
        Proizvodjac: this.form.vendor,
        TipLicence: this.form.licence_type,
        Napomena: this.form.notes,
        StatusId: Number(this.form.StatusId),
        CompanyId: Number(localStorage.getItem("CompanyId")),
        Date: this.form.purchaseDate
          ? new Date(`${this.form.purchaseDate}T${timePart}Z`).toISOString()
          : null,

        Expired: this.form.expiryDate
          ? new Date(`${this.form.expiryDate}T${timePart}Z`).toISOString()
          : null,
      }


      try {
        if (this.editMode) {
          // ========== EDIT ==========
          await this.$axios.put(
            'Software/Edit',
            {
              ...payload,
              Id: this.selectedId
            }
          )

          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m47'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          })

        } else {
          // ========== ADD ==========
          await this.$axios.post(
            'Software/Add',
            payload
          )

          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m45'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          })
        }

        this.dialog = false
        await this.fetchSoftwares()

      } catch (err) {
        console.error(this.$t('User_message.m46'), err)

        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.editMode
            ? this.$t('User_message.m48')
            : this.$t('User_message.m46'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      }
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
      this.$nextTick(() => {
        this.$refs.softwareForm?.resetValidation()
      })
    },
    resetForm() {
      this.form = {
        name: "",
        version: "",
        vendor: "",
        licence_type: "",
        notes: "",
        StatusId: 1,
        purchaseDate: null,
        expiryDate: null,
      };
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return format(new Date(dateStr), "dd.MM.yyyy");
      } catch {
        return dateStr;
      }
    },
  },
};
</script>